/**
 * Session Manager
 * Manages multi-user sessions and remembered users for the application.
 *
 * Features:
 * - Track multiple logged-in sessions (multi-cookie approach)
 * - Remember users for quick login (avatar picker)
 * - Session switching with event notifications
 * - Automatic token refresh per session
 * - Per-tab session isolation using sessionStorage
 *
 * Per-Tab Session Isolation:
 * - Each browser tab maintains its own active session via sessionStorage
 * - localStorage stores the "last used" session for new tabs
 * - When a tab gains focus, it updates localStorage with its active session
 * - This allows multiple tabs to be logged in as different users simultaneously
 */

import { ApiFactory } from '../api/agwasuite-api-factory';
import { adoptIssuedTabSession } from '../utility/session-storage-keys';
import { ApiLogin } from '../api/core/agwasuite-api-login';
import { posDb } from '../database/database';
import type {
  RememberedUser,
  SessionInfo,
  SessionEvent,
  SessionEventListener,
} from '../datatypes/session-types';
import { userDirectoryInstance } from '../global-data/user-directory-instance';

const REMEMBERED_USERS_KEY = 'remembered-users';
const MAX_REMEMBERED_USERS = 10;
// THE ACCOUNT THIS TAB IS ACTING AS - not a session, despite its former name.
// Its value is derived from the user id (see createAccountKey), it is shared across tabs on
// purpose, and it is publicly computable from the X-AGWA-User-Id header. That is all correct for
// what it does: it lets a tab remember WHICH logged-in account it is, and the product deliberately
// supports being two different users in two tabs (architect, 2026-09-12: "the userid hex is for
// handling a different user on a different tab which is allowed").
//
// It was called `agwa-active-session` until #365, and that name caused a real defect: the
// session-ownership work in #362 was briefed to send this as the per-tab session identity, which
// it is not. The genuine per-tab session is `agwa-tab-session-id` (TAB_SESSION_ID_STORAGE_KEY in
// utility/session-storage-keys.ts), issued by the server, which is what the lock system keys on.
// A duplicated or reopened tab arrives with a copy of both; it discards the session id at load and
// keeps these account keys, so it acts as the user of the tab it copied (D-136).
const ACCOUNT_STORAGE_KEY = 'agwa-active-account'; // per-tab account key
const USERID_STORAGE_KEY = 'agwa-active-userid'; // per-tab user ID
const LOCAL_ACCOUNT_KEY = 'agwa-last-account'; // browser-wide, so a new tab inherits the account
const LOCAL_USERID_KEY = 'agwa-last-userid'; // browser-wide, for new tabs

/**
 * Result of loading sessions - includes client-determined activeSessionId
 */
export interface LoadSessionsResult {
  sessions: SessionInfo[];
  /** The active session ID as determined by client-side storage */
  activeSessionId?: string;
}

/**
 * Singleton session manager for multi-user session handling
 */
class SessionManagerImpl {
  private listeners: Set<SessionEventListener> = new Set();
  private refreshTimers: Map<string, number> = new Map();
  private _currentSessionId: string | null = null;
  private _sessions: SessionInfo[] = [];
  private _visibilityListenerAttached = false;

  constructor() {
    // Initialize from sessionStorage (per-tab) or localStorage (for new tabs)
    this._initializeSessionFromStorage();
    // Set up visibility listener to update localStorage when tab gains focus
    this._setupVisibilityListener();
  }

  /**
   * Initialize the active session from storage.
   * Priority: sessionStorage (per-tab) > localStorage (last used)
   */
  private _initializeSessionFromStorage(): void {
    // First check sessionStorage (per-tab)
    const tabSession = sessionStorage.getItem(ACCOUNT_STORAGE_KEY);
    const tabUserId = sessionStorage.getItem(USERID_STORAGE_KEY);
    if (tabSession) {
      this._currentSessionId = tabSession;
      // Ensure ApiFactory also has the userId for CSRF
      if (tabUserId) {
        ApiFactory.setAuthToken(undefined, tabUserId);
      }
      return;
    }

    // Fall back to localStorage (for new tabs)
    const lastSession = localStorage.getItem(LOCAL_ACCOUNT_KEY);
    const lastUserId = localStorage.getItem(LOCAL_USERID_KEY);
    // BOTH OR NEITHER. The account key selects the cookie and the user id is the CSRF header;
    // a tab holding the first without the second would send authenticated requests that every
    // state-changing endpoint refuses for the missing header, including the unload release,
    // silently. Restoring neither leaves the tab plainly signed out, which it can recover from.
    if (lastSession && lastUserId) {
      this._currentSessionId = lastSession;
      // Store in sessionStorage so this tab now has its own copy
      sessionStorage.setItem(ACCOUNT_STORAGE_KEY, lastSession);
      sessionStorage.setItem(USERID_STORAGE_KEY, lastUserId);
      ApiFactory.setAuthToken(undefined, lastUserId);
    }
  }

  /**
   * Set up visibility change listener to update localStorage when tab gains focus.
   * This ensures "open in new tab" inherits the current tab's session.
   */
  private _setupVisibilityListener(): void {
    if (this._visibilityListenerAttached) return;
    this._visibilityListenerAttached = true;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this._currentSessionId) {
        // Update localStorage so new tabs get this session
        localStorage.setItem(LOCAL_ACCOUNT_KEY, this._currentSessionId);
        // Also sync userId for new tabs
        const userId = sessionStorage.getItem(USERID_STORAGE_KEY);
        if (userId) {
          localStorage.setItem(LOCAL_USERID_KEY, userId);
        }
      }
    });
  }

  /**
   * Persist the current session to storage (both per-tab and for new tabs)
   */
  private _persistSession(sessionId: string | null, userId?: string): void {
    if (sessionId) {
      sessionStorage.setItem(ACCOUNT_STORAGE_KEY, sessionId);
      localStorage.setItem(LOCAL_ACCOUNT_KEY, sessionId);
      if (userId) {
        sessionStorage.setItem(USERID_STORAGE_KEY, userId);
        localStorage.setItem(LOCAL_USERID_KEY, userId);
      }
    } else {
      sessionStorage.removeItem(ACCOUNT_STORAGE_KEY);
      sessionStorage.removeItem(USERID_STORAGE_KEY);
      // Note: We don't clear localStorage here - it should retain the last used session
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Public Getters
  // ─────────────────────────────────────────────────────────────────────────────

  get currentSessionId(): string | null {
    return this._currentSessionId;
  }

  get sessions(): readonly SessionInfo[] {
    return this._sessions;
  }

  get activeSession(): SessionInfo | undefined {
    return this._sessions.find((s) => s.sessionId === this._currentSessionId);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Event Subscription
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Subscribe to session events
   */
  subscribe(listener: SessionEventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: SessionEvent): void {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (err) {
        console.error('Session event listener error:', err);
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Session Loading
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Load all active sessions from the server.
   * Session selection is fully client-managed:
   * 1. Check sessionStorage (per-tab) for current session
   * 2. Validate against server's list of valid sessions
   * 3. If invalid, try localStorage (last-used from another tab)
   * 4. If still invalid, pick first available session
   */
  async loadSessions(): Promise<LoadSessionsResult> {
    try {
      const api = ApiFactory.anonymous(ApiLogin);
      const result = await api.getSessions();

      if (result.success && result.data) {
        this._sessions = result.data.sessions;

        // Self-correction: validate stored session against server's valid sessions
        const validSessionIds = new Set(this._sessions.map((s) => s.sessionId));

        // 1. Check sessionStorage (per-tab) first
        const tabSession = sessionStorage.getItem(ACCOUNT_STORAGE_KEY);
        if (tabSession && validSessionIds.has(tabSession)) {
          this._currentSessionId = tabSession;
          // Ensure userId is set from matching session
          const matchingSession = this._sessions.find(
            (s) => s.sessionId === tabSession
          );
          if (matchingSession) {
            sessionStorage.setItem(
              USERID_STORAGE_KEY,
              matchingSession.loginUserId
            );
            ApiFactory.setAuthToken(undefined, matchingSession.loginUserId);
          }
        } else {
          // 2. Try localStorage (last-used from another tab)
          const lastSession = localStorage.getItem(LOCAL_ACCOUNT_KEY);
          if (lastSession && validSessionIds.has(lastSession)) {
            this._currentSessionId = lastSession;
            sessionStorage.setItem(ACCOUNT_STORAGE_KEY, lastSession);
            // Also set userId from matching session
            const matchingSession = this._sessions.find(
              (s) => s.sessionId === lastSession
            );
            if (matchingSession) {
              sessionStorage.setItem(
                USERID_STORAGE_KEY,
                matchingSession.loginUserId
              );
              ApiFactory.setAuthToken(undefined, matchingSession.loginUserId);
            }
          } else if (this._sessions.length > 0) {
            // 3. Fall back to first available session
            this._currentSessionId = this._sessions[0].sessionId;
            this._persistSession(
              this._currentSessionId,
              this._sessions[0].loginUserId
            );
          } else {
            // No valid sessions at all
            this._currentSessionId = null;
            sessionStorage.removeItem(ACCOUNT_STORAGE_KEY);
            sessionStorage.removeItem(USERID_STORAGE_KEY);
          }
        }

        // Pre-fetch user directory data for all sessions (for user details beyond profile images)
        const userIds = this._sessions.map((s) => s.loginUserId);
        userDirectoryInstance()
          .preFetch(userIds)
          .catch(() => {
            /* ignore errors */
          });

        // Schedule refresh timers for each session
        for (const session of this._sessions) {
          this.scheduleSessionRefresh(session);
        }

        // Update remembered users with fresh data (profile images now come from SessionInfo directly)
        await this.updateRememberedUsersFromSessions(this._sessions);

        return {
          sessions: result.data.sessions,
          activeSessionId: this._currentSessionId ?? undefined,
        };
      }

      return { sessions: [], activeSessionId: undefined };
    } catch (err) {
      console.error('Failed to load sessions:', err);
      return { sessions: [], activeSessionId: undefined };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Session Switching
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Switch to a different active session
   * Emits session-switching before and session-switched after
   * Session is persisted to sessionStorage (per-tab) and localStorage (for new tabs)
   */
  async switchSession(
    sessionId: string
  ): Promise<{ success: boolean; error?: string }> {
    if (sessionId === this._currentSessionId) {
      return { success: true }; // Already on this session
    }

    const previousSessionId = this._currentSessionId;

    // Emit switching event so caches can clear
    this.emit({
      type: 'session-switching',
      sessionId: previousSessionId ?? undefined,
    });

    try {
      const api = ApiFactory.anonymous(ApiLogin);
      const result = await api.switchSession(sessionId);

      if (result.success && result.data?.success) {
        this._currentSessionId = sessionId;

        // ADOPT THE FRESH TAB SESSION the switch issued, discarding the one this tab was using.
        // A tab session belongs to a USER: the server proves it by matching its owner against the
        // authenticated caller, so the outgoing account's session stops proving the instant the
        // switch completes and this tab would otherwise be left unable to hold a lock at all. The
        // server ended the outgoing session and released its locks before issuing this (#366).
        adoptIssuedTabSession(result.data.tabSessionId);

        // Persist to storage (per-tab and for new tabs) with userId
        const userId = result.data.loginCredentials?.loginUserId;
        this._persistSession(sessionId, userId);

        // Update the sessions array with active flag and any new data from switch response
        this._sessions = this._sessions.map((s) => {
          if (s.sessionId === sessionId && result.data?.session) {
            // Merge updated session data from switch response
            return {
              ...s,
              ...result.data.session,
              isActive: true,
            };
          }
          return {
            ...s,
            isActive: s.sessionId === sessionId,
          };
        });

        // Update API auth context (userId stored in sessionStorage for per-tab isolation)
        if (result.data.loginCredentials) {
          ApiFactory.setAuthToken(
            undefined,
            result.data.loginCredentials.loginUserId
          );

          // Build profile data for event listeners (no IndexedDB storage needed)
          const creds = result.data.loginCredentials;
          const profileData = {
            authProvider: 'internal' as const,
            username: creds.username,
            displayName: creds.displayName,
            emailAddress: creds.emailAddress ?? undefined,
            token: undefined, // Using httpOnly cookies
            expiresUtc: creds.expiresUtc,
            isTwoFactorEnabled: creds.isTwoFactorEnabled,
            claims: {},
            userId: creds.loginUserId,
            loginGroupId: creds.loginGroupId ?? undefined,
            loginGroupName: creds.loginGroupName ?? undefined,
            securityContext: creds.securityContext ?? undefined,
          };

          // Emit event with profile data so listeners can update without page reload
          this.emit({
            type: 'session-switched',
            sessionId,
            userId: result.data.session?.loginUserId,
            profileData,
          });
        } else {
          this.emit({
            type: 'session-switched',
            sessionId,
            userId: result.data.session?.loginUserId,
          });
        }

        return { success: true };
      }

      return {
        success: false,
        error: result.data?.errorMessage ?? result.error ?? 'Switch failed',
      };
    } catch (err) {
      console.error('Failed to switch session:', err);
      return { success: false, error: String(err) };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Session Logout
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Logout a specific session
   */
  async logoutSession(sessionId: string): Promise<void> {
    try {
      const api = ApiFactory.anonymous(ApiLogin);
      await api.logoutSession(sessionId);

      // Clear refresh timer
      this.clearSessionRefresh(sessionId);

      // Remove from local sessions
      this._sessions = this._sessions.filter((s) => s.sessionId !== sessionId);

      // If this was the active session, clear storage and switch to another if available
      if (this._currentSessionId === sessionId) {
        if (this._sessions.length > 0) {
          // Switch to the first remaining session
          this._currentSessionId = this._sessions[0].sessionId;
          this._persistSession(
            this._sessions[0].sessionId,
            this._sessions[0].loginUserId
          );
        } else {
          this._currentSessionId = null;
          this._persistSession(null);
        }
      }

      this.emit({ type: 'session-removed', sessionId });
    } catch (err) {
      console.error('Failed to logout session:', err);
    }
  }

  /**
   * Logout all sessions
   */
  async logoutAllSessions(): Promise<void> {
    try {
      const api = ApiFactory.anonymous(ApiLogin);
      await api.logoutAllSessions();

      // Clear all refresh timers
      this.refreshTimers.forEach((timerId) => clearTimeout(timerId));
      this.refreshTimers.clear();

      this._sessions = [];
      this._currentSessionId = null;

      // Clear storage (both session and user ID)
      this._persistSession(null);
      localStorage.removeItem(LOCAL_ACCOUNT_KEY);
      localStorage.removeItem(LOCAL_USERID_KEY);

      this.emit({ type: 'sessions-cleared' });
    } catch (err) {
      console.error('Failed to logout all sessions:', err);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Token Refresh
  // ─────────────────────────────────────────────────────────────────────────────

  private scheduleSessionRefresh(session: SessionInfo): void {
    // Clear any existing timer for this session
    this.clearSessionRefresh(session.sessionId);

    const expiresAt = new Date(session.expiresUtc).getTime();
    const now = Date.now();
    const refreshTime = expiresAt - now - 3600000; // 1 hour before expiry

    if (refreshTime > 0) {
      const timerId = window.setTimeout(() => {
        void this.refreshSession(session.sessionId);
      }, refreshTime);
      this.refreshTimers.set(session.sessionId, timerId);
    }
  }

  private clearSessionRefresh(sessionId: string): void {
    const timerId = this.refreshTimers.get(sessionId);
    if (timerId) {
      clearTimeout(timerId);
      this.refreshTimers.delete(sessionId);
    }
  }

  private async refreshSession(_sessionId: string): Promise<void> {
    // To refresh a specific session, we need to temporarily switch to it
    // This is a limitation of the current API design
    // For now, we'll just reload all sessions which will get fresh expiry times
    await this.loadSessions();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Remembered Users (for login page avatar picker)
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Get all remembered users for the login page
   */
  async getRememberedUsers(): Promise<RememberedUser[]> {
    try {
      const record =
        await posDb.master.getRecord<RememberedUser[]>(REMEMBERED_USERS_KEY);
      return record?.value ?? [];
    } catch {
      return [];
    }
  }

  /**
   * Add or update a remembered user
   * Called after successful login
   */
  async rememberUser(
    user: Omit<RememberedUser, 'lastLoginUtc'>
  ): Promise<void> {
    try {
      const users = await this.getRememberedUsers();

      // Remove existing entry for this user
      const filtered = users.filter((u) => u.userId !== user.userId);

      // Add at the beginning (most recent)
      const updated: RememberedUser[] = [
        { ...user, lastLoginUtc: new Date().toISOString() },
        ...filtered,
      ].slice(0, MAX_REMEMBERED_USERS);

      await posDb.master.setRecord(REMEMBERED_USERS_KEY, updated);
      // Notify listeners that remembered users changed
      this.emit({ type: 'remembered-users-changed' });
    } catch (err) {
      console.error('Failed to remember user:', err);
    }
  }

  /**
   * Forget a specific user (remove from remembered users)
   */
  async forgetUser(userId: string): Promise<void> {
    try {
      const users = await this.getRememberedUsers();
      const filtered = users.filter((u) => u.userId !== userId);
      await posDb.master.setRecord(REMEMBERED_USERS_KEY, filtered);
      // Notify listeners that remembered users changed
      this.emit({ type: 'remembered-users-changed' });
    } catch (err) {
      console.error('Failed to forget user:', err);
    }
  }

  /**
   * Clear all remembered users
   */
  async clearRememberedUsers(): Promise<void> {
    try {
      await posDb.master.deleteRecord(REMEMBERED_USERS_KEY);
      // Notify listeners that remembered users changed
      this.emit({ type: 'remembered-users-changed' });
    } catch (err) {
      console.error('Failed to clear remembered users:', err);
    }
  }

  /**
   * Update remembered users with fresh data from sessions
   */
  private async updateRememberedUsersFromSessions(
    sessions: SessionInfo[]
  ): Promise<void> {
    try {
      const rememberedUsers = await this.getRememberedUsers();

      for (const session of sessions) {
        // Profile image URL is now directly included in SessionInfo from the server
        const profileImageUrl = session.profileImageUrl;

        const existing = rememberedUsers.find(
          (u) => u.userId === session.loginUserId
        );
        if (existing) {
          // Update with fresh data
          existing.displayName = session.displayName;
          existing.username = session.username;
          existing.emailAddress = session.emailAddress;
          existing.profileImageUrl = profileImageUrl;
        } else {
          // Add new remembered user
          rememberedUsers.unshift({
            userId: session.loginUserId,
            sessionId: session.sessionId,
            username: session.username,
            displayName: session.displayName,
            emailAddress: session.emailAddress,
            profileImageUrl,
            lastLoginUtc: new Date().toISOString(),
          });
        }
      }

      // Trim to max and save
      await posDb.master.setRecord(
        REMEMBERED_USERS_KEY,
        rememberedUsers.slice(0, MAX_REMEMBERED_USERS)
      );
      // Notify listeners that remembered users changed
      this.emit({ type: 'remembered-users-changed' });
    } catch (err) {
      console.error('Failed to update remembered users:', err);
    }
  }

  /**
   * Create session ID from user ID (first 8 chars of GUID without dashes)
   */
  createAccountKey(userId: string): string {
    return userId.replace(/-/g, '').substring(0, 8);
  }
}

// Export singleton instance
export const sessionManager = new SessionManagerImpl();
